import React, { useEffect, useState } from 'react';
import { FIPHeader } from '../components/FIPHeader';
import { FIPHero } from '../components/FIPHero';
import { FIPFooter } from '../components/FIPFooter';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Check,
  BookOpen,
  Wrench,
  HardHat,
  AlertTriangle,
  Shield,
  Users,
  DollarSign,
  FileText,
  MessageCircle,
  Award,
  Star,
  ArrowUp
} from 'lucide-react';

const CHECKOUT_URL = 'https://pay.routerfy.com/c/01k9qtctgem89zzktgpvzw6d0f';

const FIPLanding = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 800);

      // Fade-in animations
      const sections = document.querySelectorAll('.fade-in-section');
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          section.classList.add('is-visible');
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // initial trigger
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const modules = [
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: 'Módulo 1',
      subtitle: 'Fundamentos da Impermeabilização',
      description: 'Base teórica essencial para compreender os princípios da impermeabilização'
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: 'Módulo 2',
      subtitle: 'Tipos de Impermeabilização e Critérios de Escolha',
      description: 'Aprenda a escolher o sistema ideal para cada situação'
    },
    {
      icon: <HardHat className="w-8 h-8" />,
      title: 'Módulo 3',
      subtitle: 'Aplicação Prática em Obras',
      description: 'Execução real em obra - do planejamento à entrega'
    },
    {
      icon: <AlertTriangle className="w-8 h-8" />,
      title: 'Módulo 4',
      subtitle: 'Patologias e Diagnóstico',
      description: 'Identifique e corrija falhas de impermeabilização'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Módulo 5',
      subtitle: 'Segurança, Documentação e Qualidade',
      description: 'Padrões de execução e controle de qualidade'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Módulo 6',
      subtitle: 'Gestão de Obras e Equipes',
      description: 'Lidere projetos e equipes com eficiência profissional'
    }
  ];

  const bonuses = [
    {
      icon: <DollarSign className="w-10 h-10" />,
      title: 'Curso Bônus: Precificação Inteligente',
      description: 'Aprenda a precificar seus serviços de forma competitiva e lucrativa'
    },
    {
      icon: <FileText className="w-10 h-10" />,
      title: 'Modelos de Checklist e Documentação NBZ',
      description: 'Templates prontos para padronizar e profissionalizar suas entregas'
    },
    {
      icon: <MessageCircle className="w-10 h-10" />,
      title: 'Acesso à Comunidade Técnica de Alunos',
      description: 'Troque experiências e tire dúvidas com outros profissionais'
    }
  ];

  const targetAudience = [
    'Engenheiros, arquitetos e mestres de obras que querem elevar o padrão técnico',
    'Autônomos que desejam profissionalizar seu serviço e cobrar corretamente',
    'Construtoras que buscam padronizar e reduzir falhas em impermeabilização'
  ];

  const differentials = [
    {
      icon: <Award className="w-12 h-12" />,
      title: '32 anos de experiência real',
      description: 'Décadas de atuação prática em impermeabilização'
    },
    {
      icon: <Star className="w-12 h-12" />,
      title: 'Conteúdo ministrado por Érico NBZ',
      description: 'Especialista reconhecido no mercado'
    },
    {
      icon: <HardHat className="w-12 h-12" />,
      title: 'Aulas gravadas em obra',
      description: 'Aprenda com casos reais, não apenas teoria'
    },
    {
      icon: <MessageCircle className="w-12 h-12" />,
      title: 'Suporte técnico e comunidade ativa',
      description: 'Tire suas dúvidas com quem realmente entende'
    },
    {
      icon: <Award className="w-12 h-12" />,
      title: 'Certificado de conclusão',
      description: 'Certificado NBZ Educação reconhecido no mercado'
    }
  ];

  const testimonials = [
    {
      text: 'Depois do FIP, consegui padronizar os serviços da equipe e reduzir 80% dos retrabalhos.',
      author: 'Carlos Nogueira',
      role: 'Engenheiro Civil'
    },
    {
      text: 'Finalmente entendi o porquê de tantas falhas nas impermeabilizações. Hoje, consigo corrigir e evitar.',
      author: 'Juliana Mendes',
      role: 'Arquiteta'
    },
    {
      text: 'Não é um curso teórico — é um manual de campo com experiência real.',
      author: 'Ricardo Pires',
      role: 'Mestre de Obras'
    }
  ];

  return (
    <div className="fip-landing">
      <FIPHeader />

      <main>
        {/* Hero (com padding responsivo) */}
        <div className="pt-24"> {/* garante espaço para header fixo */}
          <FIPHero />
        </div>

        {/* Section 1 - A Dor e a Oportunidade */}
        <section className="fade-in-section py-16 bg-white">
          <div className="container-fip px-4 sm:px-6 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl lg:text-4xl font-bold text-primary mb-6">
                  Um erro de impermeabilização pode custar mais do que toda a obra.
                </h2>
                <div className="space-y-4 text-base md:text-lg text-gray-700 leading-relaxed">
                  <p>
                    Quem trabalha com construção civil sabe: o problema não é só infiltração — é retrabalho, custo, perda de credibilidade e clientes insatisfeitos.
                  </p>
                  <p>
                    Enquanto muitos tentam resolver na base da tentativa e erro, os profissionais que dominam a impermeabilização são os mais procurados e mais valorizados do mercado.
                  </p>
                  <p>
                    O Curso FIP foi criado justamente para isso: te tirar do básico e te levar ao nível de especialista em execução, diagnóstico e gestão.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-square bg-gray-200 rounded-2xl overflow-hidden shadow-xl max-w-[520px] mx-auto">
                  <img 
                    src="./Profissional.png" 
                    alt="Profissional em obra realizando impermeabilização"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2 - O que Você Vai Aprender */}
        <section className="fade-in-section py-16 bg-light-gray">
          <div className="container-fip px-4 sm:px-6 lg:px-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">O que Você Vai Aprender</h2>
              <p className="text-lg md:text-xl text-gray-600">6 módulos + bônus exclusivos para unir teoria, prática e gestão</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {modules.map((module, index) => (
                <Card key={index} className="p-5 md:p-6 lg:p-6 hover:shadow-lg transition-all duration-300 border border-blue-100 hover:border-accent transform hover:-translate-y-1">
                  <div className="text-primary mb-4">{module.icon}</div>
                  <h3 className="text-lg md:text-xl font-bold text-primary mb-2">{module.title}</h3>
                  <h4 className="text-md md:text-lg font-semibold text-gray-800 mb-3">{module.subtitle}</h4>
                  <p className="text-gray-600 text-sm md:text-base">{module.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Section 3 - Bônus Exclusivos */}
        <section className="fade-in-section py-16 bg-white">
          <div className="container-fip px-4 sm:px-6 lg:px-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Bônus Exclusivos – Profissional Completo</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {bonuses.map((bonus, index) => (
                <Card key={index} className="p-6 text-center hover:shadow-xl transition-all duration-300 border-2 border-accent/30 hover:border-accent">
                  <div className="text-accent mx-auto mb-4 flex justify-center">{bonus.icon}</div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3">{bonus.title}</h3>
                  <p className="text-gray-600 text-sm md:text-base">{bonus.description}</p>
                </Card>
              ))}
            </div>
            <div className="text-center">
              <Button 
                size="lg" 
                className="bg-accent hover:bg-accent-hover text-primary font-bold text-lg md:text-xl px-6 md:px-8 py-3 md:py-4 rounded-xl shadow-lg cta-pulse w-full sm:w-auto"
                onClick={() => window.open(CHECKOUT_URL, '_blank')}
              >
                Garantir minha vaga + bônus agora
              </Button>
            </div>
          </div>
        </section>

        {/* Section 4 - Para Quem É Este Curso */}
        <section className="fade-in-section py-16 bg-light-gray">
          <div className="container-fip px-4 sm:px-6 lg:px-16">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">Para quem é este curso</h2>
              <div className="space-y-4">
                {targetAudience.map((item, index) => (
                  <div key={index} className="flex flex-col sm:flex-row items-start gap-4 bg-white p-4 sm:p-6 rounded-xl shadow-md">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                        <Check className="w-5 h-5 text-primary" />
                      </div>
                    </div>
                    <p className="text-base md:text-lg text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section 5 - Diferenciais NBZ */}
        <section className="fade-in-section py-16 bg-white">
          <div className="container-fip px-4 sm:px-6 lg:px-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Por que escolher a NBZ Educação</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {differentials.slice(0, 3).map((item, index) => (
                <div key={index} className="text-center px-4">
                  <div className="text-primary mx-auto mb-4 flex justify-center">{item.icon}</div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 max-w-3xl mx-auto">
              {differentials.slice(3).map((item, index) => (
                <div key={index} className="text-center px-4">
                  <div className="text-primary mx-auto mb-4 flex justify-center">{item.icon}</div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 6 - Resultados Reais */}
        <section className="fade-in-section py-16 bg-primary text-white">
          <div className="container-fip px-4 sm:px-6 lg:px-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Resultados Reais</h2>
              <p className="text-lg md:text-xl opacity-90">O que nossos alunos dizem sobre o FIP</p>
            </div>
            <div className="relative max-w-4xl mx-auto">
              <Card className="p-8 md:p-12 bg-white/10 backdrop-blur-sm border-white/20 min-h-[260px] flex flex-col justify-center">
                <div className="text-center">
                  <div className="flex justify-center mb-4 space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                    ))}
                  </div>
                  <p className="text-lg md:text-2xl italic mb-4 md:mb-6 leading-relaxed">
                    "{testimonials[currentTestimonial].text}"
                  </p>
                  <p className="text-base md:text-xl font-bold">{testimonials[currentTestimonial].author}</p>
                  <p className="text-sm md:text-lg opacity-80">{testimonials[currentTestimonial].role}</p>
                </div>
              </Card>
              <div className="flex justify-center gap-2 mt-4">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${currentTestimonial === index ? 'bg-accent w-8' : 'bg-white/50'}`}
                    aria-label={`Depoimento ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section 7 - Certificação Profissional */}
        <section className="fade-in-section py-16 bg-light-gray">
          <div className="container-fip px-4 sm:px-6 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div className="order-2 md:order-1">
                <div className="aspect-[4/4] bg-gray-200 rounded-2xl overflow-hidden shadow-xl max-w-[520px] mx-auto">
                  <img 
                    src="./Certificado.png" 
                    alt="Certificado NBZ Educação"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="order-1 md:order-2">
                <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                  Certificação Profissional NBZ Educação
                </h2>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  Ao concluir o curso, você recebe o Certificado NBZ Educação, reconhecendo sua formação técnica e prática como Impermeabilizador Profissional — um diferencial que agrega valor imediato ao seu currículo e aos seus serviços.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 8 - Garantia e Convite Final */}
        <section className="fade-in-section py-16 bg-gradient-to-br from-blue-50 to-white">
          <div className="container-fip px-4 sm:px-6 lg:px-16">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-5xl font-bold text-primary mb-6">
                Domine a impermeabilização na prática
              </h2>
              <p className="text-base md:text-xl text-gray-700 mb-6 leading-relaxed">
                A NBZ está há mais de 30 anos no mercado e formou dezenas de profissionais que hoje lideram equipes e executam obras sem retrabalho.
              </p>
              <p className="text-base md:text-xl text-gray-700 mb-8 leading-relaxed">
                Se você quer garantir qualidade, segurança e reconhecimento profissional, o momento é agora.
              </p>
              <Button 
                size="lg" 
                className="bg-accent hover:bg-accent-hover text-primary font-bold text-lg md:text-xl px-6 md:px-12 py-3 md:py-4 rounded-xl shadow-2xl cta-pulse transform hover:scale-105 transition-all duration-300 w-full sm:w-auto"
                onClick={() => window.open(CHECKOUT_URL, '_blank')}
              >
                Quero me inscrever agora
              </Button>
            </div>
          </div>
        </section>
      </main>

      <FIPFooter />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-14 h-14 bg-accent hover:bg-accent-hover text-primary rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 z-50"
          aria-label="Voltar ao topo"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default FIPLanding;
